import React, { useState } from 'react';
import { Dialog, DialogType, DialogFooter, DefaultButton, SearchBox, Text, Stack } from '@fluentui/react';
import { FiGrid, FiBarChart2, FiPieChart, FiTrendingUp, FiActivity, FiClock, FiUsers, FiPhone, FiCheckCircle, FiXCircle, FiFolder } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import './WidgetCatalog.css';

// Catalogue de widgets disponibles.
// `name` and `description` are kept as English fallbacks for legacy callers that
// don't go through i18n; the UI itself reads `nameKey` / `descKey` via t().
export const WIDGET_CATALOG = [
    // ========================================
    // WIDGETS DU DASHBOARD PAR DÉFAUT
    // ========================================
    {
        id: 'total-calls',
        name: 'Total Calls', nameKey: 'TOTAL_CALLS',
        description: 'Total number of calls received', descKey: 'WIDGET_DESC_TOTAL_CALLS',
        category: 'dashboard',
        icon: FiPhone,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: { queryId: 'TOTAL_CALLS_TODAY' }
    },
    {
        id: 'calls-answered',
        name: 'Answered Calls', nameKey: 'ANSWERED_CALLS',
        description: 'Number of calls handled', descKey: 'WIDGET_DESC_ANSWERED_CALLS',
        category: 'dashboard',
        icon: FiCheckCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: { queryId: 'CALLS_ANSWERED' }
    },
    {
        id: 'calls-abandoned',
        name: 'Abandoned Calls', nameKey: 'ABANDONED_CALLS',
        description: 'Number of abandoned calls', descKey: 'WIDGET_DESC_ABANDONED_CALLS',
        category: 'dashboard',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'CALLS_ABANDONED' }
    },
    {
        id: 'abandonment-rate',
        name: 'Abandonment Rate', nameKey: 'ABANDONMENT_RATE',
        description: 'Percentage of abandoned calls', descKey: 'WIDGET_DESC_ABANDONMENT_RATE',
        category: 'dashboard',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'ABANDONMENT_RATE' }
    },
    {
        id: 'avg-wait-time',
        name: 'Average Wait Time', nameKey: 'AVERAGE_WAIT_TIME',
        description: 'Average time before answer', descKey: 'WIDGET_DESC_AVG_WAIT_TIME',
        category: 'dashboard',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: { queryId: 'AVG_WAIT_TIME' }
    },
    {
        id: 'avg-handling-time',
        name: 'Average Handling Time', nameKey: 'AVG_HANDLING_TIME',
        description: 'Average handling time (AHT)', descKey: 'WIDGET_DESC_AHT',
        category: 'dashboard',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: { queryId: 'AVG_HANDLING_TIME' }
    },
    {
        id: 'avg-talk-time',
        name: 'Average Talk Time', nameKey: 'AVG_TALK_TIME',
        description: 'Average conversation duration', descKey: 'WIDGET_DESC_AVG_TALK_TIME',
        category: 'dashboard',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: { queryId: 'AVG_TALK_TIME' }
    },
    {
        id: 'service-level',
        name: 'Service Level', nameKey: 'SERVICE_LEVEL',
        description: 'Percentage answered under target time', descKey: 'WIDGET_DESC_SERVICE_LEVEL',
        category: 'dashboard',
        icon: FiActivity,
        defaultSize: { w: 3, h: 4 },
        type: 'gauge',
        color: '#0078D4',
        dataSource: { queryId: 'SERVICE_LEVEL' }
    },
    {
        id: 'fcr',
        name: 'First Call Resolution', nameKey: 'FCR',
        description: 'First call resolution rate', descKey: 'WIDGET_DESC_FCR',
        category: 'dashboard',
        icon: FiCheckCircle,
        defaultSize: { w: 4, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: { queryId: 'FIRST_CALL_RESOLUTION' }
    },
    {
        id: 'calls-by-hour',
        name: 'Calls by Hour', nameKey: 'CALLS_BY_HOUR',
        description: 'Hourly call distribution', descKey: 'WIDGET_DESC_CALLS_BY_HOUR',
        category: 'dashboard',
        icon: FiBarChart2,
        defaultSize: { w: 6, h: 5 },
        type: 'bar',
        color: '#0078D4',
        dataSource: { queryId: 'CALLS_BY_HOUR' }
    },

    // ========================================
    // WIDGETS GÉNÉRIQUES (Catalogue)
    // ========================================
    // Métriques
    {
        id: 'widget-total-calls',
        name: 'Total Calls', nameKey: 'TOTAL_CALLS',
        description: 'Display total number of calls', descKey: 'WIDGET_DESC_TOTAL_CALLS',
        category: 'metrics',
        icon: FiPhone,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4'
    },
    {
        id: 'widget-answered-calls',
        name: 'Answered Calls', nameKey: 'ANSWERED_CALLS',
        description: 'Calls that were answered', descKey: 'WIDGET_DESC_ANSWERED_CALLS',
        category: 'metrics',
        icon: FiCheckCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#107C10'
    },
    {
        id: 'widget-abandoned-calls',
        name: 'Abandoned Calls', nameKey: 'ABANDONED_CALLS',
        description: 'Calls that were abandoned', descKey: 'WIDGET_DESC_ABANDONED_CALLS',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438'
    },
    {
        id: 'widget-outbound-calls',
        name: 'Outbound Calls', nameKey: 'OUTBOUND_CALLS',
        description: 'Total outbound calls', descKey: 'WIDGET_DESC_OUTBOUND_CALLS',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8764B8'
    },
    {
        id: 'widget-average-wait-time',
        name: 'Average Wait Time', nameKey: 'AVERAGE_WAIT_TIME',
        description: 'Average time callers wait', descKey: 'WIDGET_DESC_AVG_WAIT_TIME',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#FF8C00'
    },
    {
        id: 'widget-average-ringing-time',
        name: 'Average Ringing Time', nameKey: 'AVG_RINGING_TIME',
        description: 'Average ringing time before agent answers', descKey: 'WIDGET_DESC_AVG_RINGING_TIME',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#FF8C00'
    },
    {
        id: 'widget-avg-agents-per-call',
        name: 'Agents Alerted per Call', nameKey: 'AVG_AGENTS_PER_CALL',
        description: 'Average number of agents alerted before a call is taken', descKey: 'WIDGET_DESC_AVG_AGENTS_PER_CALL',
        category: 'metrics',
        icon: FiUsers,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8764B8'
    },
    {
        id: 'widget-overflow-rate',
        name: 'Queue Overflow Rate', nameKey: 'OVERFLOW_RATE',
        description: 'Percentage of queued calls that overflowed to another queue', descKey: 'WIDGET_DESC_OVERFLOW_RATE',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438'
    },
    {
        id: 'widget-quick-drop-count',
        name: 'Quick Drops', nameKey: 'QUICK_DROPS',
        description: 'Calls dropped before reaching a queue or within the threshold', descKey: 'WIDGET_DESC_QUICK_DROPS',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438'
    },
    {
        id: 'widget-quick-drop-rate',
        name: 'Quick Drop Rate', nameKey: 'QUICK_DROP_RATE',
        description: 'Percentage of calls dropped before reaching a queue or within the threshold', descKey: 'WIDGET_DESC_QUICK_DROP_RATE',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438'
    },
    {
        id: 'widget-overflow-paths',
        name: 'Top Queue Overflow Paths', nameKey: 'OVERFLOW_PATHS',
        description: 'Most frequent queue traversal paths', descKey: 'WIDGET_DESC_OVERFLOW_PATHS',
        category: 'analytics',
        icon: FiActivity,
        defaultSize: { w: 6, h: 5 },
        type: 'bar',
        color: '#FF8C00'
    },
    {
        id: 'widget-overflow-matrix',
        name: 'Queue Overflow Matrix', nameKey: 'OVERFLOW_MATRIX',
        description: 'Hop counts between source and destination queues', descKey: 'WIDGET_DESC_OVERFLOW_MATRIX',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 6, h: 5 },
        type: 'table',
        color: '#0078D4'
    },
    {
        id: 'widget-service-level',
        name: 'Service Level', nameKey: 'SERVICE_LEVEL',
        description: 'Service level percentage', descKey: 'WIDGET_DESC_SERVICE_LEVEL',
        category: 'metrics',
        icon: FiActivity,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00BCF2'
    },
    {
        id: 'widget-avg-inbound-call-duration',
        name: 'Average Inbound Duration', nameKey: 'AVERAGE_INBOUND_CALL_DURATION',
        description: 'Average duration of inbound calls', descKey: 'WIDGET_DESC_AVG_INBOUND_DURATION',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#498205'
    },
    {
        id: 'widget-avg-outbound-call-duration',
        name: 'Average Outbound Duration', nameKey: 'AVERAGE_OUTBOUND_CALL_DURATION',
        description: 'Average duration of outbound calls', descKey: 'WIDGET_DESC_AVG_OUTBOUND_DURATION',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8E8CD8'
    },
    {
        id: 'widget-transferred-calls',
        name: 'Transferred Calls', nameKey: 'TRANSFERRED_CALLS',
        description: 'Number of transferred calls', descKey: 'WIDGET_DESC_TRANSFERRED_CALLS',
        category: 'metrics',
        icon: FiActivity,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8764B8'
    },
    {
        id: 'widget-waiting-time',
        name: 'Waiting Time Limits', nameKey: 'WIDGET_WAITING_TIME_LIMITS',
        description: 'Configured waiting time thresholds', descKey: 'WIDGET_DESC_WAITING_TIME_LIMITS',
        category: 'thresholds',
        icon: FiClock,
        defaultSize: { w: 4, h: 2 },
        type: 'metric',
        color: '#FF8C00'
    },
    {
        id: 'widget-talking-time',
        name: 'Talking Time Limits', nameKey: 'WIDGET_TALKING_TIME_LIMITS',
        description: 'Configured talking time thresholds', descKey: 'WIDGET_DESC_TALKING_TIME_LIMITS',
        category: 'thresholds',
        icon: FiClock,
        defaultSize: { w: 4, h: 2 },
        type: 'metric',
        color: '#0078D4'
    },
    {
        id: 'widget-abandon-time',
        name: 'Abandon Time Limits', nameKey: 'WIDGET_ABANDON_TIME_LIMITS',
        description: 'Configured abandon time thresholds', descKey: 'WIDGET_DESC_ABANDON_TIME_LIMITS',
        category: 'thresholds',
        icon: FiClock,
        defaultSize: { w: 4, h: 2 },
        type: 'metric',
        color: '#D13438'
    },

    // Graphiques
    {
        id: 'widget-call-volume-hourly',
        name: 'Call Volume (Hourly)', nameKey: 'WIDGET_CALL_VOLUME_HOURLY',
        description: 'Hourly call volume distribution', descKey: 'WIDGET_DESC_CALL_VOLUME_HOURLY',
        category: 'charts',
        icon: FiBarChart2,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#0078D4'
    },
    {
        id: 'widget-call-volume-daily',
        name: 'Call Volume (Daily)', nameKey: 'WIDGET_CALL_VOLUME_DAILY',
        description: 'Daily call volume trends', descKey: 'WIDGET_DESC_CALL_VOLUME_DAILY',
        category: 'charts',
        icon: FiTrendingUp,
        defaultSize: { w: 6, h: 4 },
        type: 'line',
        color: '#107C10'
    },
    {
        id: 'widget-call-status-distribution',
        name: 'Call Status Distribution', nameKey: 'CALL_STATUS_DISTRIBUTION',
        description: 'Distribution by call status', descKey: 'WIDGET_DESC_CALL_STATUS_DIST',
        category: 'charts',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#8764B8'
    },
    {
        id: 'widget-thresholds-distribution',
        name: 'Call Response Distribution', nameKey: 'WIDGET_CALL_RESPONSE_DIST',
        description: 'Distribution of calls by response speed', descKey: 'WIDGET_DESC_CALL_RESPONSE_DIST',
        category: 'charts',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#107C10'
    },
    {
        id: 'widget-calls-by-flow',
        name: 'Calls by Flow', nameKey: 'CALLS_BY_FLOW',
        description: 'Call distribution by flow', descKey: 'WIDGET_DESC_CALLS_BY_FLOW',
        category: 'charts',
        icon: FiBarChart2,
        defaultSize: { w: 4, h: 4 },
        type: 'bar',
        color: '#FF8C00'
    },
    {
        id: 'widget-peak-hours',
        name: 'Peak Hours Analysis', nameKey: 'PEAK_HOURS',
        description: 'Identify peak call hours', descKey: 'WIDGET_DESC_PEAK_HOURS',
        category: 'charts',
        icon: FiActivity,
        defaultSize: { w: 12, h: 5 },
        type: 'heatmap',
        color: '#D13438'
    },
    {
        id: 'widget-calls-per-agent',
        name: 'Calls per Agent', nameKey: 'CALLS_BY_AGENT',
        description: 'Agent performance metrics', descKey: 'WIDGET_DESC_CALLS_BY_AGENT',
        category: 'charts',
        icon: FiUsers,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#00BCF2'
    },
    {
        id: 'widget-avg-ringing-per-agent',
        name: 'Average Ringing Time by Agent', nameKey: 'AVG_RINGING_TIME_BY_AGENT',
        description: 'Average ringing time before each agent answers', descKey: 'WIDGET_DESC_AVG_RINGING_BY_AGENT',
        category: 'charts',
        icon: FiClock,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#00BCF2'
    },
    {
        id: 'widget-calls-by-queue',
        name: 'Calls by Queue', nameKey: 'CALLS_BY_QUEUE',
        description: 'Queue distribution', descKey: 'WIDGET_DESC_CALLS_BY_QUEUE',
        category: 'charts',
        icon: FiGrid,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#498205'
    },

    // Analytics
    {
        id: 'widget-queue-kpi-summary',
        name: 'Flow KPI Summary', nameKey: 'FLOW_KPI_SUMMARY',
        description: 'Comprehensive KPI metrics by flow (transposed view)', descKey: 'WIDGET_DESC_FLOW_KPI',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 12, h: 6 },
        type: 'table',
        color: '#0078D4',
        dataSource: { queryId: 'queue-kpi-summary' }
    },
    {
        id: 'widget-month-to-date-summary',
        name: 'Month-to-Date Summary', nameKey: 'MONTH_TO_DATE_SUMMARY',
        description: 'Month-to-date KPI summary with service level, answer rate, and performance metrics by flow', descKey: 'WIDGET_DESC_MTD',
        category: 'analytics',
        icon: FiClock,
        defaultSize: { w: 12, h: 7 },
        type: 'table',
        color: '#00B294',
        dataSource: { queryId: 'month-to-date-summary' }
    },
    {
        id: 'widget-calls-served-time-ranges',
        name: 'Calls Served by Time Ranges', nameKey: 'CALLS_SERVED_TIME_RANGES',
        description: 'Distribution of served calls across time ranges by flow', descKey: 'WIDGET_DESC_CALLS_SERVED_TR',
        category: 'analytics',
        icon: FiCheckCircle,
        defaultSize: { w: 12, h: 5 },
        type: 'table',
        color: '#107C10',
        dataSource: { queryId: 'calls-served-by-time-ranges' }
    },
    {
        id: 'widget-calls-abandoned-time-ranges',
        name: 'Abandoned Calls by Time Ranges', nameKey: 'CALLS_ABANDONED_TIME_RANGES',
        description: 'Distribution of abandoned calls across time ranges by flow', descKey: 'WIDGET_DESC_CALLS_ABANDONED_TR',
        category: 'analytics',
        icon: FiXCircle,
        defaultSize: { w: 12, h: 5 },
        type: 'table',
        color: '#D13438',
        dataSource: { queryId: 'calls-abandoned-by-time-ranges' }
    },
    {
        id: 'widget-daily-sla-stats',
        name: 'Daily SLA Statistics', nameKey: 'DAILY_SLA_STATS',
        description: 'Daily served & redirected calls with time-bucket distribution and cumulative SLA percentages', descKey: 'WIDGET_DESC_DAILY_SLA',
        category: 'analytics',
        icon: FiActivity,
        defaultSize: { w: 12, h: 7 },
        type: 'sla-table',
        color: '#0078D4',
        dataSource: { queryId: 'daily-sla-stats' }
    },

    // Charts avancés
    {
        id: 'widget-area-chart',
        name: 'Area Chart', nameKey: 'WIDGET_AREA_CHART',
        description: 'Area chart with multiple series', descKey: 'WIDGET_DESC_AREA_CHART',
        category: 'charts',
        icon: FiTrendingUp,
        defaultSize: { w: 6, h: 4 },
        type: 'area',
        color: '#0078D4'
    },
    {
        id: 'widget-scatter-plot',
        name: 'Scatter Plot', nameKey: 'WIDGET_SCATTER_PLOT',
        description: 'Scatter plot for correlation analysis', descKey: 'WIDGET_DESC_SCATTER_PLOT',
        category: 'charts',
        icon: FiActivity,
        defaultSize: { w: 6, h: 4 },
        type: 'scatter',
        color: '#8764B8'
    },
    {
        id: 'widget-radar-chart',
        name: 'Radar Chart', nameKey: 'WIDGET_RADAR_CHART',
        description: 'Multi-axis radar/spider chart', descKey: 'WIDGET_DESC_RADAR_CHART',
        category: 'charts',
        icon: FiGrid,
        defaultSize: { w: 5, h: 5 },
        type: 'radar',
        color: '#0078D4'
    },
    {
        id: 'widget-heatmap-advanced',
        name: 'Advanced Heatmap', nameKey: 'WIDGET_HEATMAP',
        description: 'Color-coded data heatmap', descKey: 'WIDGET_DESC_HEATMAP',
        category: 'charts',
        icon: FiActivity,
        defaultSize: { w: 8, h: 5 },
        type: 'heatmapAdvanced',
        color: '#D13438'
    },
    {
        id: 'widget-funnel-chart',
        name: 'Funnel Chart', nameKey: 'WIDGET_FUNNEL',
        description: 'Conversion funnel visualization', descKey: 'WIDGET_DESC_FUNNEL',
        category: 'charts',
        icon: FiTrendingUp,
        defaultSize: { w: 5, h: 5 },
        type: 'funnel',
        color: '#107C10'
    },
    {
        id: 'widget-bullet-chart',
        name: 'Bullet Chart', nameKey: 'WIDGET_BULLET',
        description: 'Progress towards target', descKey: 'WIDGET_DESC_BULLET',
        category: 'charts',
        icon: FiActivity,
        defaultSize: { w: 6, h: 3 },
        type: 'bullet',
        color: '#00BCF2'
    },
    {
        id: 'widget-treemap',
        name: 'TreeMap', nameKey: 'WIDGET_TREEMAP',
        description: 'Hierarchical data visualization', descKey: 'WIDGET_DESC_TREEMAP',
        category: 'charts',
        icon: FiGrid,
        defaultSize: { w: 6, h: 5 },
        type: 'treemap',
        color: '#498205'
    },

    // Métriques avancées
    {
        id: 'widget-kpi-comparison',
        name: 'KPI Comparison', nameKey: 'WIDGET_KPI_COMPARE',
        description: 'Compare KPIs side by side', descKey: 'WIDGET_DESC_KPI_COMPARE',
        category: 'metrics',
        icon: FiActivity,
        defaultSize: { w: 4, h: 3 },
        type: 'kpiCompare',
        color: '#107C10'
    },
    {
        id: 'widget-progress-bar',
        name: 'Progress Bar', nameKey: 'WIDGET_PROGRESS_BAR',
        description: 'Visual progress indicator', descKey: 'WIDGET_DESC_PROGRESS_BAR',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 4, h: 3 },
        type: 'progress',
        color: '#8764B8'
    },
    {
        id: 'widget-sparkline',
        name: 'Sparkline', nameKey: 'WIDGET_SPARKLINE',
        description: 'Compact trend line', descKey: 'WIDGET_DESC_SPARKLINE',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 3, h: 2 },
        type: 'sparkline',
        color: '#0078D4'
    },
    {
        id: 'widget-status-indicator',
        name: 'Status Indicator', nameKey: 'WIDGET_STATUS_INDICATOR',
        description: 'Real-time status monitoring', descKey: 'WIDGET_DESC_STATUS_INDICATOR',
        category: 'metrics',
        icon: FiActivity,
        defaultSize: { w: 3, h: 3 },
        type: 'status',
        color: '#107C10'
    },

    // Analytics avancés
    {
        id: 'widget-timeline',
        name: 'Timeline', nameKey: 'WIDGET_TIMELINE',
        description: 'Event timeline visualization', descKey: 'WIDGET_DESC_TIMELINE',
        category: 'analytics',
        icon: FiClock,
        defaultSize: { w: 12, h: 4 },
        type: 'timeline',
        color: '#0078D4'
    },
    {
        id: 'widget-geographic-map',
        name: 'Geographic Map', nameKey: 'WIDGET_GEO_MAP',
        description: 'Location-based data map', descKey: 'WIDGET_DESC_GEO_MAP',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 8, h: 6 },
        type: 'geomap',
        color: '#00BCF2'
    },
    {
        id: 'widget-sankey',
        name: 'Sankey Diagram', nameKey: 'WIDGET_SANKEY',
        description: 'Flow and movement visualization', descKey: 'WIDGET_DESC_SANKEY',
        category: 'analytics',
        icon: FiActivity,
        defaultSize: { w: 8, h: 6 },
        type: 'sankey',
        color: '#8764B8'
    },
    {
        id: 'widget-network-graph',
        name: 'Network Graph', nameKey: 'WIDGET_NETWORK',
        description: 'Relationship network diagram', descKey: 'WIDGET_DESC_NETWORK',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 8, h: 6 },
        type: 'network',
        color: '#498205'
    },
    {
        id: 'widget-waterfall-chart',
        name: 'Waterfall Chart', nameKey: 'WIDGET_WATERFALL',
        description: 'Cumulative effect visualization', descKey: 'WIDGET_DESC_WATERFALL',
        category: 'analytics',
        icon: FiBarChart2,
        defaultSize: { w: 8, h: 5 },
        type: 'waterfall',
        color: '#FF8C00'
    },
    {
        id: 'widget-candlestick',
        name: 'Candlestick Chart', nameKey: 'WIDGET_CANDLESTICK',
        description: 'OHLC data visualization', descKey: 'WIDGET_DESC_CANDLESTICK',
        category: 'analytics',
        icon: FiTrendingUp,
        defaultSize: { w: 8, h: 5 },
        type: 'candlestick',
        color: '#D13438'
    }
];

const CATEGORIES = [
    { key: 'all',       labelKey: 'WIDGET_CAT_ALL',       icon: FiGrid },
    { key: 'dashboard', labelKey: 'WIDGET_CAT_DASHBOARD', icon: FiFolder },
    { key: 'metrics',   labelKey: 'WIDGET_CAT_METRICS',   icon: FiActivity },
    { key: 'charts',    labelKey: 'WIDGET_CAT_CHARTS',    icon: FiBarChart2 },
    { key: 'analytics', labelKey: 'WIDGET_CAT_ANALYTICS', icon: FiTrendingUp }
];

const WidgetCatalog = ({ isOpen, onDismiss, onSelectWidget }) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const localizedName = (w) => (w.nameKey ? t(w.nameKey) : w.name) || w.name;
    const localizedDesc = (w) => (w.descKey ? t(w.descKey) : w.description) || w.description;

    const filteredWidgets = WIDGET_CATALOG.filter(widget => {
        const q = searchQuery.toLowerCase();
        const matchesSearch =
            localizedName(widget).toLowerCase().includes(q) ||
            localizedDesc(widget).toLowerCase().includes(q);
        const matchesCategory = selectedCategory === 'all' || widget.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const widgetsByCategory = filteredWidgets.reduce((acc, widget) => {
        if (!acc[widget.category]) {
            acc[widget.category] = [];
        }
        acc[widget.category].push(widget);
        return acc;
    }, {});

    const categoryLabel = (key) => {
        const cat = CATEGORIES.find(c => c.key === key);
        return cat ? t(cat.labelKey) : key;
    };

    const handleWidgetClick = (widget) => {
        onSelectWidget(widget);
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={onDismiss}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <FiGrid size={24} />
                        <Text variant="xLarge">{t('WIDGET_CATALOG_TITLE')}</Text>
                    </Stack>
                ),
                subText: t('WIDGET_CATALOG_SUBTITLE')
            }}
            modalProps={{
                isBlocking: false,
                className: 'widget-catalog-dialog'
            }}
            minWidth="900px"
        >
            <div className="widget-catalog-container">
                <Stack tokens={{ childrenGap: 16 }} className="catalog-header">
                    <SearchBox
                        placeholder={t('WIDGET_CATALOG_SEARCH')}
                        value={searchQuery}
                        onChange={(_, newValue) => setSearchQuery(newValue || '')}
                        className="catalog-search"
                    />

                    <Stack horizontal tokens={{ childrenGap: 8 }} className="analytics-category-tabs">
                        {CATEGORIES.map(category => {
                            const Icon = category.icon;
                            return (
                                <div
                                    key={category.key}
                                    className={`analytics-category-tab ${selectedCategory === category.key ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category.key)}
                                >
                                    <Icon size={18} />
                                    <span>{t(category.labelKey)}</span>
                                    <span className="widget-count">
                                        {category.key === 'all'
                                            ? WIDGET_CATALOG.length
                                            : WIDGET_CATALOG.filter(w => w.category === category.key).length
                                        }
                                    </span>
                                </div>
                            );
                        })}
                    </Stack>
                </Stack>

                <div className="widget-catalog-grid">
                    {selectedCategory === 'all' ? (
                        Object.entries(widgetsByCategory).map(([category, widgets]) => (
                            <div key={category} className="category-section">
                                <Text variant="large" className="category-title">
                                    {categoryLabel(category)}
                                </Text>
                                <div className="widget-grid">
                                    {widgets.map(widget => (
                                        <WidgetCard
                                            key={widget.id}
                                            widget={widget}
                                            onClick={() => handleWidgetClick(widget)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="widget-grid">
                            {filteredWidgets.map(widget => (
                                <WidgetCard
                                    key={widget.id}
                                    widget={widget}
                                    onClick={() => handleWidgetClick(widget)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {filteredWidgets.length === 0 && (
                    <div className="no-results">
                        <FiGrid size={48} />
                        <Text variant="large">{t('WIDGET_CATALOG_NO_RESULTS')}</Text>
                        <Text variant="small">{t('WIDGET_CATALOG_NO_RESULTS_HINT')}</Text>
                    </div>
                )}
            </div>

            <DialogFooter>
                <DefaultButton onClick={onDismiss} text={t('CLOSE')} />
            </DialogFooter>
        </Dialog>
    );
};

const WidgetCard = ({ widget, onClick }) => {
    const { t } = useTranslation();
    const Icon = widget.icon;
    const name = widget.nameKey ? t(widget.nameKey) : widget.name;
    const description = widget.descKey ? t(widget.descKey) : widget.description;

    return (
        <div
            className="widget-card"
            onClick={onClick}
            style={{ borderLeftColor: widget.color }}
        >
            <div className="widget-card-header">
                <div className="widget-icon" style={{ backgroundColor: widget.color + '20' }}>
                    <Icon size={24} color={widget.color} />
                </div>
            </div>
            <div className="widget-card-body">
                <Text variant="medium" className="widget-name">{name}</Text>
                <Text variant="small" className="widget-description">{description}</Text>
            </div>
            <div className="widget-card-footer">
                <span className="widget-type-badge">{widget.type}</span>
            </div>
        </div>
    );
};

export default WidgetCatalog;
